import {
  Badge, Button, Container, Group, Pagination, Stack,
  Table, Text, Title, Tooltip,
} from '@mantine/core';
import { IconFilterOff, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ClientType, LifecycleStatus } from '../api/types';
import { useAgreements } from '../hooks/useAgreements';
import { ALLOCATION_OPTIONS, allocLabel } from '../utils/allocation';
import { ExportCsvButton } from '../components/ExportCsvButton';
import { useStaffOptions } from '../hooks/useAgreementInteractions';
import {
  ColumnDateRangeFilter, ColumnMultiSelectFilter,
  ColumnTextFilter, hasActiveFilters,
} from '../components/ColumnFilters';
import { PepDot, UspDot } from '../components/PepUsDot';
import { NO_STATUS_SELECTED_HINT, StatusFilterPills } from '../components/StatusFilterPills';
import { PendingCRBadge } from '../components/PendingCRBadge';
import {
  SortableTh, nextSort, sortParam, type SortState,
} from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';

const PAGE_SIZE = 50;
type AgreementSortKey =
  'code' | 'mnemonic' | 'type' | 'nature' | 'residence' | 'saa' | 'mifir' | 'iaa' | 'pep' | 'usp' | 'rm' | 'status';

export function AgreementsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  // #277 (2026-06-08): cumulative multi-status filter. Default = Active only.
  // The "Deleted" pill is conceptually a separate axis (deleted_at IS NOT
  // NULL) but rendered alongside the lifecycle status pills for a single
  // unified filter surface.
  // #51 (D4): Prospects are shown by default alongside Active; the user can
  // deselect the Prospect pill to hide them.
  const [statusFilter, setStatusFilter] = useState<LifecycleStatus[]>(['Active', 'Prospect']);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // ── #46 Wave 1 (2026-06-12): per-column predicate filters (C-132) ──────
  // These replaced the old global search box + service Select: the Code
  // text filter matches code_1 (typing "CIIF" or "CEC" covers the old
  // service dropdown), Mnemonic has its own column filter. Every control
  // traces to a query param (C-131); page resets to 1 on every change.
  const [codeQ, setCodeQ] = useState('');
  const [mnemonicQ, setMnemonicQ] = useState('');
  // Closed-set filters are multi-select (C-132 amendment 2026-06-12).
  const [clientTypeF, setClientTypeF] = useState<string[]>([]);
  const [natureF, setNatureF] = useState<string[]>([]);
  const [residenceF, setResidenceF] = useState<string[]>([]);
  const [mifirQ, setMifirQ] = useState('');
  const [iaaFrom, setIaaFrom] = useState('');
  const [iaaTo, setIaaTo] = useState('');
  // C-132 Amendment 3 (2026-06-12, Pedro): people-pickers are multi too.
  const [rmF, setRmF] = useState<string[]>([]);
  // #46 follow-up (2026-06-12, Pedro's request): filter by the SAME
  // worst-answered-signal rollup buckets that colour the PEP / USP dots.
  // Backend re-expresses the dot precedence as SQL (agreements.py).
  const [pepF, setPepF] = useState<string[]>([]);
  const [uspF, setUspF] = useState<string[]>([]);
  // P19(a) (team feedback fb 5, 2026-10-10): list agreements by SAA.
  const [saaF, setSaaF] = useState<string[]>([]);

  // P41 (2026-10-15): header sort, server-side across pages; null = the default (code).
  // Clears when you navigate away (F6, revised by Pedro 2026-10-15).
  const [sort, setSortState] = useState<SortState<AgreementSortKey> | null>(null);
  function onSort(k: AgreementSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const filtersActive = hasActiveFilters({
    codeQ, mnemonicQ, clientTypeF, natureF, residenceF, mifirQ, iaaFrom, iaaTo,
    rmF, pepF, uspF, saaF,
  });
  function clearFilters() {
    setCodeQ(''); setMnemonicQ(''); setClientTypeF([]); setNatureF([]);
    setResidenceF([]); setMifirQ(''); setIaaFrom(''); setIaaTo(''); setRmF([]);
    setPepF([]); setUspF([]); setSaaF([]);
    setPage(1);
  }
  // Every filter change returns to page 1 — a filter on page 3 of the old
  // result set would otherwise show an empty page of the new one.
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const staffQ = useStaffOptions();
  const rmOptions = (Array.isArray(staffQ.data) ? staffQ.data : [])
    .map((u) => ({ value: u.id, label: u.display_name }));

  const columnFilters = {
    code_q: codeQ || undefined,
    mnemonic_q: mnemonicQ || undefined,
    client_type: clientTypeF.length > 0 ? (clientTypeF as ClientType[]) : undefined,
    investor_nature: natureF.length > 0 ? natureF : undefined,
    residence: residenceF.length > 0 ? residenceF : undefined,
    mifir_q: mifirQ || undefined,
    iaa_from: iaaFrom || undefined,
    iaa_to: iaaTo || undefined,
    named_rm_user_id: rmF.length > 0 ? rmF : undefined,
    pep_agg: pepF.length > 0 ? pepF : undefined,
    usp_agg: uspF.length > 0 ? uspF : undefined,
    strategic_allocation: saaF.length > 0 ? saaF : undefined,
  };

  // #39 (2026-06-11, Pedro's four-bucket decision): zero pills selected
  // means an EMPTY result — skip the fetch entirely (the API's no-param
  // form is reserved for legacy callers meaning "all alive").
  const hasSelection = statusFilter.length > 0 || includeDeleted;
  const { data, isLoading, error } = useAgreements({
    status: statusFilter.length > 0 ? statusFilter : undefined,
    include_deleted: includeDeleted,
    ...columnFilters,
    sort: sortParam(sort),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }, { enabled: hasSelection });
  // useDeleteAgreement / useRestoreAgreement / useConfirmDelete were removed
  // 2026-06-02 (Task #245) — delete/restore moved exclusively to the detail
  // page top-right. List rows no longer trigger destructive actions.
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // Build the export URL with the current filter set so the CSV mirrors what
  // the user is looking at. Same-origin GET; the browser session cookie carries
  // auth automatically. No client-side state mutation.
  const exportHref = (() => {
    const params = new URLSearchParams();
    // #277 (2026-06-08): repeat the status param for each selected pill.
    for (const s of statusFilter) params.append('status', s);
    if (includeDeleted) params.set('include_deleted', 'true');
    // #46 (2026-06-12): the CSV mirrors the column filters too — the
    // backend export endpoint shares the filter helper with the list.
    // Arrays expand to repeated keys (multi-select, C-132 amendment).
    for (const [k, v] of Object.entries(columnFilters)) {
      if (!v) continue;
      if (Array.isArray(v)) for (const item of v) params.append(k, String(item));
      else params.set(k, String(v));
    }
    return `/agreements/export.xlsx${params.toString() ? `?${params}` : ''}`;
  })();

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Agreements</Title>
          <Group gap="sm">
            {/* The shared list export (Admin-only, greyed with the reason; #39). */}
            <ExportCsvButton href={exportHref} hasSelection={hasSelection} />
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/agreements/new')}>
              New agreement
            </Button>
          </Group>
        </Group>

        {/* #46 (2026-06-12): the old global search box + service Select
            were replaced by the per-column header filters below (C-132).
            Typing in the Code filter covers the service axis (CIIF/CEC
            are part of code_1). */}

        {/* #277 (2026-06-08): cumulative status pills via the shared
            StatusFilterPills component. Default = Active only. */}
        <StatusFilterPills
          entity="agreement"
          statuses={statusFilter}
          onStatusesChange={(next) => { setStatusFilter(next); setPage(1); }}
          includeDeleted={includeDeleted}
          onIncludeDeletedChange={(next) => { setIncludeDeleted(next); setPage(1); }}
          // P37 (2026-10-15): no "Closed" pill — Closed is retired for agreements
          // (migration 0046 forbids it; C-166). Accounts and Policies keep theirs:
          // a terminated policy and its accounts are Closed.
          availableStatuses={['Prospect', 'Active', 'Inactive']}
        />

        {error && <Text c="red">{error.detail}</Text>}

        {/* D2-polish 3: list columns now mirror the detail-page summary bar
            (Baluarte Code · Mnemonic · Type · Investor nature · Residence ·
            SAA [P19(a), 2026-10-10] · MIFIR · IAA initial). When an agreement is Inactivo, the IAA
            column shows the close_date instead of the IAA initial date,
            matching the summary-bar edge case. */}
        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            {/* P41 (2026-10-15): every header sorts (server-side, across pages). */}
            <Table.Tr>
              <SortableTh label="Baluarte Code" col="code" sort={sort} onSort={onSort} filtered={!!codeQ} w={130} />
              <SortableTh label="Mnemonic" col="mnemonic" sort={sort} onSort={onSort} filtered={!!mnemonicQ} w={120} />
              <SortableTh label="Type" col="type" sort={sort} onSort={onSort} filtered={clientTypeF.length > 0} w={120} />
              <SortableTh label="Investor nature" col="nature" sort={sort} onSort={onSort} filtered={natureF.length > 0} w={130} />
              <SortableTh label="Residence" col="residence" sort={sort} onSort={onSort} filtered={residenceF.length > 0} w={120} />
              <SortableTh label="SAA" col="saa" sort={sort} onSort={onSort} filtered={saaF.length > 0} w={90} />
              <SortableTh label="MIFIR" col="mifir" sort={sort} onSort={onSort} filtered={!!mifirQ} w={150} />
              <SortableTh label="IAA initial" col="iaa" sort={sort} onSort={onSort} filtered={iaaFrom !== '' || iaaTo !== ''} w={130} />
              {/* Beta v3 polish B: PEP / USP rollup dots — same logic
                  as the Agreement detail-page summary badges. Filterable
                  by the same rollup buckets since 2026-06-12. */}
              <SortableTh label="PEP" col="pep" sort={sort} onSort={onSort} filtered={pepF.length > 0} w={95} />
              <SortableTh label="USP" col="usp" sort={sort} onSort={onSort} filtered={uspF.length > 0} w={95} />
              <SortableTh label="Named RM" col="rm" sort={sort} onSort={onSort} filtered={rmF.length > 0} w={140} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={200} />
              {/* Actions column removed 2026-06-02 (Task #245) — delete/restore
                  moved to the detail page top-right exclusively. */}
            </Table.Tr>
            {/* #46 Wave 1 (2026-06-12): per-column filter row (C-132).
                PEP / USP are derived rollups (no server filter); Status
                is owned by the pills (one axis, one control). */}
            <Table.Tr>
              <Table.Th>
                <ColumnTextFilter value={codeQ} onChange={paged(setCodeQ)}
                  placeholder="CIIF-001…" ariaLabel="Filter by Baluarte code" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={mnemonicQ} onChange={paged(setMnemonicQ)}
                  placeholder="PESIAS…" ariaLabel="Filter by mnemonic" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={clientTypeF} onChange={paged(setClientTypeF)}
                  options={[
                    { value: 'Particular', label: 'Particular' },
                    { value: 'Empresa', label: 'Empresa' },
                  ]}
                  allLabel="All" ariaLabel="Filter by client type" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={natureF} onChange={paged(setNatureF)}
                  options={[
                    { value: 'NP', label: 'NP — Non-professional' },
                    { value: 'P', label: 'P — Professional' },
                    { value: 'CE', label: 'CE — Eligible counterparty' },
                  ]}
                  allLabel="All" ariaLabel="Filter by investor nature" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={residenceF} onChange={paged(setResidenceF)}
                  options={[
                    { value: 'R', label: 'R — Resident' },
                    { value: 'NR', label: 'NR — Non-resident' },
                  ]}
                  allLabel="All" ariaLabel="Filter by residence" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={saaF} onChange={paged(setSaaF)}
                  options={ALLOCATION_OPTIONS}
                  allLabel="All" ariaLabel="Filter by SAA" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={mifirQ} onChange={paged(setMifirQ)}
                  placeholder="MIFIR id…" ariaLabel="Filter by MIFIR id" />
              </Table.Th>
              <Table.Th>
                {/* Filters on the IAA-initial signing date. For Inactive
                    rows the cell shows the Close date instead — the
                    filter deliberately stays on the IAA axis. */}
                <ColumnDateRangeFilter from={iaaFrom} to={iaaTo}
                  onChange={(f, t) => { setIaaFrom(f); setIaaTo(t); setPage(1); }}
                  ariaLabelPrefix="IAA initial" />
              </Table.Th>
              <Table.Th>
                {/* Bucket values mirror the dot precedence exactly
                    (PEP > Related > Não > not set). */}
                <ColumnMultiSelectFilter values={pepF} onChange={paged(setPepF)}
                  options={[
                    { value: 'PEP', label: 'PEP' },
                    { value: 'Related', label: 'Related to PEP' },
                    { value: 'Nao', label: 'No' },
                    { value: 'NotSet', label: 'Not answered' },
                  ]}
                  allLabel="All" ariaLabel="Filter by PEP rollup" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={uspF} onChange={paged(setUspF)}
                  options={[
                    { value: 'Yes', label: 'Yes' },
                    { value: 'No', label: 'No' },
                    { value: 'NotSet', label: 'Not answered' },
                  ]}
                  allLabel="All" ariaLabel="Filter by US-person rollup" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={rmF} onChange={paged(setRmF)}
                  options={rmOptions} allLabel="Any RM" ariaLabel="Filter by Named RM" />
              </Table.Th>
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {/* #39: zero pills → hint instead of data (no fetch fired). */}
            {!hasSelection && (
              <Table.Tr><Table.Td colSpan={12}><Text c="dimmed">{NO_STATUS_SELECTED_HINT}</Text></Table.Td></Table.Tr>
            )}
            {hasSelection && isLoading && <Table.Tr><Table.Td colSpan={12}><Text c="dimmed">Loading...</Text></Table.Td></Table.Tr>}
            {/* #46 / C-132: two-tier empty state — distinguish "no records
                in the selected buckets" from "no records match the column
                filters" (with the Clear affordance). */}
            {hasSelection && data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={12}>
                  {filtersActive ? (
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No agreements match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  ) : (
                    <Text c="dimmed">No agreements found.</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {hasSelection && data?.items.map((a) => {
              // D2-polish 3 follow-up: prefer close_date only when the
              // agreement is Inactive/Closed AND a CLOSE row exists; otherwise
              // fall back to IAA initial. Legacy agreements without a
              // recorded close_date still show their original signing date.
              // #273 (2026-06-08): Closed (terminal) joins Inactive (limbo)
              // as a state for which Close date supersedes IAA initial.
              const showClose =
                a.status === 'Inactive' && !!a.close_date;   // Closed retired (P37)
              const iaaCol = showClose ? (
                <Group gap={4} wrap="nowrap">
                  <Badge size="xs" variant="light" color="gray">Close</Badge>
                  <Text size="sm">{a.close_date}</Text>
                </Group>
              ) : (a.iaa_initial_date ?? <Text size="xs" c="orange.7">Pending IAA</Text>);
              const iaaLabel = showClose ? 'Close date' : 'IAA initial';
              return (
                <Table.Tr
                  key={a.id}
                  style={{ cursor: 'pointer', opacity: a.deleted_at ? 0.5 : 1 }}
                  onClick={() => navigate(`/agreements/${a.id}`)}
                >
                  <Table.Td><Text fw={500} ff="monospace">{a.code_1}</Text></Table.Td>
                  <Table.Td><Text ff="monospace">{a.client_mnemonic}</Text></Table.Td>
                  <Table.Td>{a.client_type}</Table.Td>
                  <Table.Td>{a.mifid_investor_nature ?? '—'}</Table.Td>
                  <Table.Td>{a.mifid_residence ?? '—'}</Table.Td>
                  <Table.Td>{a.strategic_allocation ? allocLabel(a.strategic_allocation) : '—'}</Table.Td>
                  <Table.Td>
                    {a.mifir_id ? (
                      <Group gap={4} wrap="nowrap">
                        <Badge size="xs" variant="light">{a.mifir_id_type ?? '—'}</Badge>
                        <Text size="sm" ff="monospace">{a.mifir_id}</Text>
                      </Group>
                    ) : '—'}
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={iaaLabel} withArrow openDelay={200}>
                      <span>{iaaCol}</span>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td ta="center"><PepDot value={a.aggregated_pep_status} /></Table.Td>
                  <Table.Td ta="center"><UspDot value={a.aggregated_us_person} /></Table.Td>
                  <Table.Td>
                    {/* #276 polish (2026-06-08): primary-tier names first;
                        truncated to N+more for compactness. Tooltip
                        exposes the full list. */}
                    {(() => {
                      const primaries = a.named_rms.filter((r) => r.is_primary);
                      const display = primaries.length > 0 ? primaries : a.named_rms;
                      if (display.length === 0) return <Text c="dimmed">—</Text>;
                      const first = display[0];
                      const rest = display.length - 1;
                      const tooltip = display.map((r) => r.display_name).join(', ');
                      return (
                        <Tooltip label={tooltip} withArrow disabled={display.length <= 1}>
                          <Text size="sm">
                            {first.display_name}
                            {rest > 0 && <Text component="span" c="dimmed"> +{rest}</Text>}
                          </Text>
                        </Tooltip>
                      );
                    })()}
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={4} align="flex-start">
                      {/* P47: the badge explains its status on hover (same words as the pills). */}
                      <StatusExplained entity="agreement" status={a.deleted_at ? 'Deleted' : a.status}>
                      {a.deleted_at
                        ? <Badge color="gray" variant="light">Deleted</Badge>
                        : <Badge
                            color={
                              a.status === 'Active' ? 'green'
                                : a.status === 'Inactive' ? 'yellow'
                                : a.status === 'Prospect' ? 'grape'  // P37: same as the pills and the header
                                : 'gray'
                            }
                            variant="light"
                          >{a.status}</Badge>}
                      </StatusExplained>
                      <PendingCRBadge crs={a.pending_change_requests} />
                    </Stack>
                  </Table.Td>
                  {/* Task #245 (2026-06-02): per-row trashcan + restore icons
                      removed. Delete/Undelete now lives exclusively on the
                      Agreement detail page top-right, where the cascade-aware
                      flow is correctly wired and the user sees a DELETED-state
                      page after deletion. Eliminates the silent-fail UX where
                      a list-page trashcan click would 409 silently when active
                      attachments existed (former Task #244). */}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}